import { useState, useEffect, useContext } from "react";
import { UserContext } from "../../Context";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROLES, SALARY_SLIP_STATUS } from "./constants";
import { usePayroll } from "./Hooks/usePayroll.js";
import { SALARY_SLIP } from "../../routes.js";
import { useParams, useLocation } from "react-router-dom";
import { Loader, Tooltip } from "../../Components/Common";
import { USER_ROLES } from "../../Constants/Strings.js";
import {
  formatCurrency,
  formatDateDDMMYYYY,
  formatMonthYear,
  toProperCase,
} from "../../Utils/index.js";
import { useSalarySlipPdf } from "./Hooks/useSalarySlipPdf.js";
import { FiDownload } from "react-icons/fi";
import { getEligibleReferrals } from "../../Services/essServices.js";
import toast from "react-hot-toast";
import { extractErrorMessage } from "../../Utils/extractErrorMessage.js";

const SalarySlipAction = () => {
  const navigate = useNavigate();
  const {
    fetchSalarySlipById,
    updateSalarySlip,
    notifyDownloadSalarySlip,
    loading,
  } = usePayroll();
  const { slipId } = useParams();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const isView = location.pathname.includes("view");
  const isEdit = location.pathname.includes("edit");
  const { downloadSalarySlipPdf } = useSalarySlipPdf();
  const [referralData, setReferralData] = useState([]);
  const [selectedReferrals, setSelectedReferrals] = useState([]);
  const [formData, setFormData] = useState({
    user: "",
    designation: "",
    start_date: "",
    end_date: "",
    basic_amount: "",
    da: "",
    hra: "",
    conveyance_allowance: "",
    medical_allowance: "",
    attendance_allowance: "",
    washing_allowance: "",
    performance_allowance: "",
    gross: "",
    employee_pf: "",
    employer_pf: "",
    employee_esic: "",
    employer_esic: "",
    pt: "",
    tds: "",
    referral_bonus: "",
    leave_deductions: "",
    other_adjustments: "",
    total_deductions: "",
    net: "",
    status: SALARY_SLIP_STATUS.INITIATED,
    remark: "",
    month_summary: "",
    leave_balances: [],
  });
  const [salarySlipData, setSalarySlipData] = useState(null);

  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (slipId) {
      fetchSalarySlipById(slipId).then((data) => {
        setFormData({
          user: { name: data.username },
          designation: data.designation,
          start_date: data.start_date,
          end_date: data.end_date,
          basic_amount: data.basic_amount,
          da: data.da,
          hra: data.hra,
          conveyance_allowance: data.conveyance_allowance,
          medical_allowance: data.medical_allowance,
          attendance_allowance: data.attendance_allowance,
          washing_allowance: data.washing_allowance,
          performance_allowance: data.performance_allowance,
          gross: data.gross,
          employee_pf: data.employee_pf,
          employer_pf: data.employer_pf,
          employee_esic: data.employee_esic,
          employer_esic: data.employer_esic,
          pt: data.pt,
          tds: data.tds,
          referral_bonus: data.referral_bonus,
          leave_deductions: data.leave_deductions,
          other_adjustments: data.other_adjustments,
          total_deductions: data.total_deductions,
          net: data.net,
          status: data.status,
          remark: data.remark,
          userDetails: data.userDetails,
          month_summary: data.month_summary,
          leave_balances: data.leave_balances || [],
        });
        setSalarySlipData(data);
      });
    }
  }, [slipId, refresh]);

  useEffect(() => {
    const fetchPendingReferrals = async () => {
      if (!formData.user?.id) return;

      try {
        const response = await getEligibleReferrals({
          referred_by: formData.user.id,
        });

        if (response.status === 200) {
          setReferralData(response.data.results);
        } else {
          toast.error(
            extractErrorMessage(response) || "Failed to fetch referrals",
          );
        }
      } catch (err) {
        toast.error(extractErrorMessage(err) || "Failed to fetch referrals");
      }
    };

    fetchPendingReferrals();
  }, [formData.user]);

  useEffect(() => {
    if (slipId) {
      fetchSalarySlipById(slipId).then((data) => {
        setFormData({
          user: { name: data.username, id: data.user },
          designation: data.designation,
          start_date: data.start_date,
          end_date: data.end_date,
          basic_amount: data.basic_amount,
          da: data.da,
          hra: data.hra,
          conveyance_allowance: data.conveyance_allowance,
          medical_allowance: data.medical_allowance,
          attendance_allowance: data.attendance_allowance,
          washing_allowance: data.washing_allowance,
          performance_allowance: data.performance_allowance,
          gross: data.gross,
          employee_pf: data.employee_pf,
          employer_pf: data.employer_pf,
          employee_esic: data.employee_esic,
          employer_esic: data.employer_esic,
          pt: data.pt,
          tds: data.tds,
          referral_bonus: data.referral_bonus,
          leave_deductions: data.leave_deductions,
          other_adjustments: data.other_adjustments,
          total_deductions: data.total_deductions,
          net: data.net,
          status: data.status,
          remark: data.remark,
          userDetails: data.userDetails,
          month_summary: data.month_summary,
          referrals: data.referrals || [],
          referrals_data: data.referrals_data || [],
          leave_balances: data.leave_balances || [],
        });
      });
    }
  }, [slipId, refresh]);

  const [showAttendancePopup, setShowAttendancePopup] = useState(false);

  useEffect(() => {
    if (!formData.referrals || referralData.length === 0) return;

    const initiallySelected = referralData.filter((ref) =>
      formData.referrals.includes(ref.id),
    );

    setSelectedReferrals(initiallySelected);
  }, [formData.referrals, referralData]);

  const handleSlipDownload = async (slipId) => {
    await notifyDownloadSalarySlip(slipId);
  };

  const earningsList = [
    { label: "Basic", value: formData.basic_amount },
    { label: "DA", value: formData.da },
    { label: "HRA", value: formData.hra },
    { label: "Conveyance", value: formData.conveyance_allowance },
    { label: "Medical", value: formData.medical_allowance },
    { label: "Attendance", value: formData.attendance_allowance },
    { label: "Washing", value: formData.washing_allowance },
    { label: "Performance", value: formData.performance_allowance },
    { label: "Gross", value: formData.gross },
    { label: "Referral Bonus", value: formData.referral_bonus },
    { label: "Other Adjustment", value: formData.other_adjustments },
  ];

  const deductionsList = [
    { label: "Employee PF", value: formData.employee_pf },
    { label: "Employer PF", value: formData.employer_pf },
    { label: "Employee ESIC", value: formData.employee_esic },
    { label: "Employer ESIC", value: formData.employer_esic },
    { label: "PT", value: formData.pt },
    { label: "TDS", value: formData.tds },
    { label: "Leave Deduction", value: formData.leave_deductions },
    { label: "Total Deduction", value: formData.total_deductions },
  ];

  const renderLeaveBalances = () => {
    const balances = formData.leave_balances;

    return (
      <div className="mt-4 w-[75%] col-span-2">
        {/* Header */}
        <h4 className="text-md font-semibold text-primary mb-3 flex items-center gap-2">
          <span className="inline-block w-1 h-5 bg-primary rounded-full"></span>
          Leave Balances
        </h4>

        {/* Card */}
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-100 shadow-sm">
          {!balances || balances.length === 0 ? (
            <p className="text-gray-500 text-sm px-4 py-3 text-center">
              No leave balance data available.
            </p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {balances.map((b, idx) => (
                  <tr
                    key={idx}
                    className="border-t first:border-t-0 hover:bg-gray-50 transition"
                  >
                    {/* Label */}
                    <td className="px-4 py-3 font-medium text-gray-600">
                      {b.category_display || b.category || "Balance"}
                    </td>

                    {/* Value */}
                    <td
                      className={`px-4 py-3 text-center text-base font-semibold ${
                        b.balance < 0 ? "text-red-500" : "text-green-600"
                      }`}
                    >
                      {b.balance}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderEmployeeDetails = () => {
    return (
      <div className="w-full md:w-[350px] mt-1.5">
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2 mt-2 ">
          <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
          Employee Details
        </h3>
        <div className="grid grid-cols-2 gap-3 mt-4">
          {[
            ["First Name", formData?.userDetails?.first_name],
            ["Last Name", formData?.userDetails?.last_name],
            ["Email", formData?.userDetails?.email],
            ["Mobile", formData?.userDetails?.mobile],
            [
              "Role",
              formData?.userDetails?.role === USER_ROLES.HR
                ? formData?.userDetails?.role
                : toProperCase(formData?.userDetails?.role),
            ],
            ["Department", formData?.userDetails?.department],
            ["Designation", formData?.userDetails?.designation],
            [
              "Date Of Joining",
              formatDateDDMMYYYY(formData?.userDetails?.date_of_joining),
            ],
          ].map(([label, value]) => (
            <div key={label}>
              <p className="text-md font-xl text-primary">{label}</p>
              <p className="font-semibold text-gray-600 mt-1.5 break-words">
                {value || "-"}
              </p>
            </div>
          ))}
        </div>

        <div className="w-full col-span-2">
          {/* Leave balances — HR/Admin/Director only */}
          {[ROLES.HR, ROLES.ADMIN_ROLE, ROLES.DIRECTOR_MANAGEMENT].includes(
            user?.role,
          ) && renderLeaveBalances()}
          {isEdit && renderReferrals()}
          {!isEdit && renderViewReferrals()}
        </div>
      </div>
    );
  };

  const generateMonthGrid = (monthSummary) => {
    if (!monthSummary) return [];

    // Get all dates in month
    const dates = Object.keys(monthSummary).sort();

    const firstDay = new Date(dates[0]).getDay();

    const grid = [];

    // Add empty cells for first day offset
    for (let i = 0; i < firstDay; i++) {
      grid.push({ empty: true });
    }

    // Add all days with status
    dates.forEach((date) => {
      grid.push({
        date,
        status: monthSummary[date],
      });
    });

    return grid;
  };

  const renderMonthSummary = () => {
    if (!formData.month_summary) return null;

    const grid = generateMonthGrid(formData.month_summary);

    const statusColor = {
      ATTENDED: "bg-green-200",
      LOP: "bg-red-200",
      NCNS: "bg-yellow-200",
      HOLIDAY: "bg-green-200",
      HALF_LOP: "bg-blue-200",
      BEFORE_JOINING: "bg-gray-200",
    };

    // Compute stats from month_summary
    const entries = Object.values(formData.month_summary);
    const attendedCount = entries.filter((s) => s === "ATTENDED").length;
    const ncnsCount = entries.filter((s) => s === "NCNS").length;
    const lopCount = entries.filter((s) => s === "LOP").length;
    const halfLopCount = entries.filter((s) => s === "HALF_LOP").length;

    return (
      <div className="w-full md:w-[300px] mt-1.5 relative">
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2 mt-2">
          <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
          Month Summary
          {/* Info Button */}
          <button
            onClick={() => setShowAttendancePopup(true)}
            className="w-5 h-5 rounded-full border border-gray-400 text-gray-500 text-xs flex items-center justify-center hover:bg-gray-100 ml-1"
            title="View attendance details"
          >
            i
          </button>
        </h3>

        {/* Week Days */}
        <div className="grid grid-cols-7 text-center font-semibold mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day}>{day}</div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {grid.map((cell, idx) => (
            <div
              key={idx}
              className={`p-2 text-center text-md font-semibold rounded ${
                cell.empty ? "" : statusColor[cell.status] || "bg-gray-100"
              }`}
              title={
                cell.empty
                  ? ""
                  : `${cell.date}: ${
                      cell.status === "NCNS" || cell.status === "LOP"
                        ? cell.status
                        : cell.status === "HALF_LOP"
                          ? "Half Day"
                          : toProperCase(cell.status)
                    }`
              }
            >
              {!cell.empty && new Date(cell.date).getDate()}
            </div>
          ))}
        </div>

        {/* Attendance Info Popup */}
        {showAttendancePopup && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={() => setShowAttendancePopup(false)}
          >
            <div
              className="bg-white rounded-xl shadow-lg p-5 w-[320px] max-w-[90vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-1">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">
                    Attendance Summary — {formatMonthYear(formData.start_date)}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formData?.userDetails?.first_name}{" "}
                    {formData?.userDetails?.last_name} ·{" "}
                    {formData?.userDetails?.designation}
                  </p>
                </div>
                <button
                  className="text-gray-400 hover:text-gray-600 text-lg leading-none ml-2"
                  onClick={() => setShowAttendancePopup(false)}
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                {[
                  {
                    label: "Days attended",
                    value: attendedCount,
                    color: "text-green-600",
                  },
                  {
                    label: "Paid Leave",
                    value: salarySlipData?.paid_leave_days ?? 0,
                    color: "text-gray-800",
                  },
                  {
                    label: "NCNS",
                    value: ncnsCount + lopCount,
                    color: "text-red-500",
                  },
                  {
                    label: "Half days",
                    value: halfLopCount,
                    color: "text-blue-600",
                  },
                  {
                    label: "LOP days",
                    value: salarySlipData?.lop_count ?? 0,
                    color: "text-red-500",
                  },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
                    <p className={`text-xl font-semibold ${color}`}>{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRemark = () => {
    return (
      <div className="w-full md:w-[300px] mt-4">
        <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
          <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
          Remark
        </h3>
        {isEdit ? (
          <textarea
            className="w-full border rounded-lg p-2 text-sm text-gray-700 outline-none focus:ring-1 focus:ring-primary resize-none"
            rows={3}
            placeholder="Add a remark..."
            value={formData.remark || ""}
            onChange={(e) =>
              setFormData({ ...formData, remark: e.target.value })
            }
          />
        ) : (
          <p className="text-sm text-gray-600 bg-gray-50 border rounded-lg p-2 min-h-[60px]">
            {formData.remark || (
              <span className="text-gray-400 italic">No remark added.</span>
            )}
          </p>
        )}
      </div>
    );
  };

  const handleSave = async (action) => {
    const payload = {
      tds: formData.tds,
      leave_deductions: formData.leave_deductions,
      referral_bonus: formData.referral_bonus,
      other_adjustments: formData.other_adjustments,
      referral_ids: selectedReferrals.map((r) => r.id),
      remark: formData.remark,
    };

    const updated = await updateSalarySlip(slipId, payload, { action });

    if (updated) {
      setRefresh((prev) => prev + 1);
      // navigate(DETAIL_SALARY_SLIP.replace(":slipId", slipId));
    }
  };

  const renderReferrals = () => (
    <div className="mt-4 w-full col-span-2 relative">
      <h4 className="text-md font-semibold text-primary mb-2">
        {referralData.length === 0
          ? "No Eligible Referrals"
          : "Eligible Referrals"}
      </h4>

      {referralData.length === 0 ? (
        <p className="text-gray-500 text-sm">
          No eligible referrals available.
        </p>
      ) : (
        <div className="border rounded-lg p-2 max-h-[30vh] overflow-y-auto">
          {referralData.map((ref) => {
            const isChecked = selectedReferrals.some((r) => r.id === ref.id);

            return (
              <label
                key={ref.id}
                className="flex items-center gap-2 mb-1 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedReferrals((prev) => [...prev, ref]);
                    } else {
                      setSelectedReferrals((prev) =>
                        prev.filter((r) => r.id !== ref.id),
                      );
                    }
                  }}
                />
                <span className="text-sm">
                  {ref.referred_person_name} – {ref.referred_position} (
                  {formatDateDDMMYYYY(ref.date_of_joining)})
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderViewReferrals = () => {
    if (!formData.referrals_data || formData.referrals_data.length === 0) {
      return (
        <div className="mt-4">
          <h4 className="text-md font-semibold text-primary mb-2">Referrals</h4>
          <p className="text-gray-500 text-sm">No referrals linked.</p>
        </div>
      );
    }

    return (
      <div className="mt-4 w-full col-span-2">
        <h4 className="text-md font-semibold text-primary mb-2">Referrals</h4>

        <div className="border rounded-lg p-2 space-y-2">
          {formData.referrals_data.map((ref) => (
            <div key={ref.id} className="p-2 rounded bg-gray-50">
              <p className="font-medium text-gray-800">
                {ref.referred_person_name}
              </p>
              <p className="text-sm text-gray-500">
                {ref.referred_position} -{" "}
                {formatDateDDMMYYYY(ref.date_of_joining)}{" "}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 md:px-4 md:py-2">
      {/* Header */}
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(SALARY_SLIP)}>
            <ArrowLeft size={23} className="text-primary" />
          </button>
          <h2 className="text-xl font-semibold text-primary">
            {isEdit ? "Edit Salary Slip" : "View Salary Slip"}
          </h2>
        </div>
        {isView && (
          <Tooltip text={"Download Salary Slip"}>
            <button
              className="px-4 py-1 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
              onClick={() => {
                downloadSalarySlipPdf(salarySlipData);
                handleSlipDownload(slipId);
              }}
            >
              <FiDownload />
            </button>
          </Tooltip>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Loader />
        </div>
      ) : (
        <>
          <div className="flex flex-col md:flex-row md:gap-8 w-full text-sm">
            {renderEmployeeDetails()}
            <div className="w-[2px] bg-gray-400 md:mr-2 mt-2 hidden md:block"></div>
            {/* Salary slip details */}
            <div className="md:mt-2 max-w-[650px] md:mb-2">
              <div className="mx-auto">
                {/* Header */}
                <div className="text-center border-b pb-1">
                  <div className="flex items-center justify-between mb-4 mt-4 md:mt-2.5">
                    <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                      <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
                      Salary Slip Details
                    </h3>
                    <span className="text-black font-normal">
                      ({formatMonthYear(formData.start_date)})
                    </span>
                  </div>
                </div>
                {/* Earnings & Deductions */}
                <div className="mt-2 grid grid-cols-2 gap-6 md:gap-12">
                  {/* Earnings */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 border-b pb-1 text-primary">
                      Earnings
                    </h3>
                    <table className="w-full text-sm md:text-md">
                      <tbody>
                        {earningsList.map((item, idx) => {
                          const fieldKeyMap = {
                            "Referral Bonus": "referral_bonus",
                            "Other Adjustment": "other_adjustments",
                          };
                          const fieldKey = fieldKeyMap[item.label];
                          const isEditable =
                            isEdit &&
                            ["Referral Bonus", "Other Adjustment"].includes(
                              item.label,
                            );
                          return (
                            <tr key={idx} className="border-b last:border-none">
                              <td className="py-1 text-primary w-[250px]">
                                {item.label}
                              </td>
                              <td className="py-1 font-medium text-right w-[150px]">
                                {isEditable ? (
                                  <input
                                    type="number"
                                    className="px-1 py-1 w-full text-right outline-none"
                                    style={{
                                      borderTop: "none",
                                      borderLeft: "none",
                                      borderRight: "none",
                                    }}
                                    value={formData[fieldKey] || ""}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        [fieldKey]: e.target.value,
                                      })
                                    }
                                  />
                                ) : (
                                  <>{formatCurrency(item.value) || 0}</>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Deductions */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 border-b pb-1 text-primary">
                      Deductions
                    </h3>
                    <table className="w-full text-sm md:text-md">
                      <tbody>
                        {deductionsList.map((item, idx) => {
                          const fieldKeyMap = {
                            TDS: "tds",
                            "Leave Deduction": "leave_deductions",
                          };
                          const fieldKey = fieldKeyMap[item.label];
                          const isEditable =
                            isEdit &&
                            ["TDS", "Leave Deduction"].includes(item.label);
                          return (
                            <tr key={idx} className="border-b last:border-none">
                              <td className="py-1 text-primary w-[250px]">
                                {item.label}
                              </td>
                              <td className="py-1 font-medium text-right w-[150px]">
                                {isEditable ? (
                                  <input
                                    type="number"
                                    className="px-2 py-1 w-full text-right outline-none"
                                    style={{
                                      borderTop: "none",
                                      borderLeft: "none",
                                      borderRight: "none",
                                    }}
                                    value={formData[fieldKey] || ""}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        [fieldKey]: e.target.value,
                                      })
                                    }
                                  />
                                ) : (
                                  <>{formatCurrency(item.value) || 0}</>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Gross & Net Pay */}
                <div className="mt-2 border-t pt-2 pb-4">
                  <div className="flex justify-between text-lg font-semibold">
                    <span className="flex justify-between text-lg font-semibold text-primary">
                      Net Salary
                    </span>
                    <span>{formatCurrency(formData.net)}</span>
                  </div>
                </div>
                {/* EDIT MODE */}
                {isEdit && (
                  <div className="mt-4 py-5 flex justify-end gap-2">
                    <button
                      className="px-6 py-2 bg-primary text-white font-semibold rounded hover:bg-green-600"
                      onClick={() => handleSave(null)}
                    >
                      Save Changes
                    </button>

                    {formData.status === SALARY_SLIP_STATUS.INITIATED &&
                      [ROLES.HR, ROLES.DIRECTOR_MANAGEMENT].includes(
                        user?.role,
                      ) && (
                        <button
                          className="px-4 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700"
                          onClick={() => handleSave("send_for_approval")}
                        >
                          Send For Approval
                        </button>
                      )}

                    {formData.status ===
                      SALARY_SLIP_STATUS.PENDING_FOR_APPROVAL &&
                      [ROLES.HR, ROLES.DIRECTOR_MANAGEMENT].includes(
                        user?.role,
                      ) && (
                        <button
                          className="px-4 py-2 bg-gray-500 text-white font-semibold rounded hover:bg-gray-600"
                          onClick={() => handleSave("revert")}
                        >
                          Revert
                        </button>
                      )}
                  </div>
                )}
                {!isEdit &&
                  formData.status === SALARY_SLIP_STATUS.PENDING_FOR_APPROVAL &&
                  [ROLES.ADMIN_ROLE, ROLES.DIRECTOR_MANAGEMENT].includes(
                    user?.role,
                  ) && (
                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        className="px-4 py-2 bg-primary text-white font-semibold rounded hover:bg-green-600"
                        onClick={() => handleSave("approve")}
                      >
                        Approve
                      </button>
                    </div>
                  )}
              </div>
            </div>
            <div className="w-[2px] bg-gray-400 md:mr-2 md:ml-2 mt-2 hidden md:block"></div>
            <div className="flex flex-col md:gap-4 md:w-[250px] w-full">
              {renderMonthSummary()}
              {renderRemark()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalarySlipAction;
